import { useTranslation } from 'react-i18next'
import { ModalShell } from '@/components/ui/ModalShell'
import { Button } from '@/components/ui/Button'
import type { MergeResult } from '@/services/merge'
import type { ResolutionSet } from '@/services/merge/resolve'
import { ConflictList } from './ConflictList'
import { useConflictResolution } from './useConflictResolution'

export interface ConflictResolverModalProps {
  open: boolean
  mergeResult: MergeResult
  onCancel: () => void
  onApply: (resolutionSet: ResolutionSet) => void
  /** Optional: escape-hatch callbacks (discard / save-as-new) */
  onDiscardMine?: () => void
  onSaveAsNew?: () => void
}

export function ConflictResolverModal({
  open,
  mergeResult,
  onCancel,
  onApply,
  onDiscardMine,
  onSaveAsNew,
}: ConflictResolverModalProps) {
  const { t } = useTranslation()
  const {
    resolutions,
    resolutionSet,
    allResolved,
    resolvedCount,
    totalConflicts,
    setChoice,
    setFieldChoice,
    acceptAllMine,
    acceptAllTheirs,
  } = useConflictResolution(mergeResult)

  function handleApply() {
    if (!allResolved) return
    onApply(resolutionSet)
  }

  const footer = (
    <div className="flex w-full items-center justify-between gap-3 flex-wrap">
      <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
        {totalConflicts === 0
          ? t('conflict.noConflictsShort', 'Sem conflitos')
          : t('conflict.resolvedCount', '{count} de {total} resolvidos', {
              count: resolvedCount,
              total: totalConflicts,
            })}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel', 'Cancelar')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleApply}
          disabled={!allResolved && totalConflicts > 0}
        >
          {t('conflict.applyAndCommit', 'Aplicar e versionar')}
        </Button>
      </div>
    </div>
  )

  return (
    <ModalShell
      open={open}
      onClose={onCancel}
      title={t('conflict.resolverTitle', 'Resolver conflitos de versão')}
      description={t(
        'conflict.resolverDesc',
        'Outro usuário publicou uma nova versão. Revise os conflitos abaixo:',
      )}
      widthClassName="max-w-2xl"
      footer={footer}
    >
      {/* Header actions */}
      {totalConflicts > 0 && (
        <div className="flex gap-2 mb-4">
          <Button variant="secondary" size="sm" onClick={acceptAllMine}>
            {t('conflict.acceptAllMine', 'Aceitar tudo: o meu')}
          </Button>
          <Button variant="secondary" size="sm" onClick={acceptAllTheirs}>
            {t('conflict.acceptAllTheirs', 'Aceitar tudo: o deles')}
          </Button>
        </div>
      )}

      {/* Column headers */}
      {totalConflicts > 0 && (
        <div className="grid grid-cols-2 gap-3 mb-3 text-[10px] font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>
          <div>{t('conflict.sideMine', 'O que eu fiz')}</div>
          <div>{t('conflict.sideTheirs', 'O que foi feito')}</div>
        </div>
      )}

      {/* Conflict list */}
      <ConflictList
        mergeResult={mergeResult}
        resolutions={resolutions}
        onChoose={setChoice}
        onFieldChoose={setFieldChoice}
      />

      {/* Escape hatches */}
      {(onDiscardMine || onSaveAsNew) && (
        <div
          className="mt-6 border-t pt-4 flex flex-wrap items-center gap-3"
          style={{ borderColor: 'var(--border)' }}
        >
          <span className="text-[11px]" style={{ color: 'var(--text-dim)' }}>
            {t('conflict.escapeHatch', 'Não quero resolver:')}
          </span>
          {onDiscardMine && (
            <Button variant="ghost" size="sm" onClick={onDiscardMine}>
              {t('conflict.discardMine', 'Descartar minhas mudanças')}
            </Button>
          )}
          {onSaveAsNew && (
            <Button variant="ghost" size="sm" onClick={onSaveAsNew}>
              {t('conflict.saveAsNew', 'Salvar como novo projeto')}
            </Button>
          )}
        </div>
      )}
    </ModalShell>
  )
}
